import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'react-hot-toast';
import Header from './components/Header';
import MarketPage from './components/MarketPage';
import TradingPanel from './components/TradingPanel';
import AddTokenModal from './components/AddTokenModal';
import PortfolioPage from './components/PortfolioPage';
import AnnouncementPage from './components/AnnouncementPage';
import { useWeb3 } from './hooks/useWeb3';
import { useBondingCurve, TokenData } from './hooks/useBondingCurve';
import { BONDING_CURVE_ADDRESS, MIGRATION_THRESHOLD } from './config/contracts';
import { fetchDbTokens, upsertTokens, insertTransaction, updateTokenMeta } from './lib/supabase';
import './App.css';

type Page = 'home' | 'trade' | 'add' | 'portfolio' | 'announce';

function App() {
  const web3 = useWeb3();
  const bonding = useBondingCurve(web3.signer, web3.provider);

  const [page, setPage] = useState<Page>('home');
  const [tokens, setTokens] = useState<TokenData[]>([]);
  const [selectedToken, setSelectedToken] = useState<TokenData | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [usdtBalance, setUsdtBalance] = useState('0');
  const [tokenBalance, setTokenBalance] = useState('0');
  const [loadingTokens, setLoadingTokens] = useState(false);

  // Load all tokens — 链上 + Supabase 双源合并
  const bondingRef = React.useRef(bonding);
  bondingRef.current = bonding;

  const loadTokens = useCallback(async () => {
    setLoadingTokens(true);
    try {
      // 1. 先从 Supabase 快速加载（不依赖钱包连接）
      const dbRows = await fetchDbTokens();
      if (dbRows.length > 0) {
        const dbTokens: TokenData[] = dbRows.map(r => ({
          tokenAddress: r.token_address,
          name: r.name,
          symbol: r.symbol,
          decimals: r.decimals,
          totalSupply: r.total_supply,
          reserveUSDT: String(r.reserve_usdt),
          tokenBalance: r.token_balance,
          currentPrice: r.current_price,
          migrated: r.migrated,
          active: r.active,
          creator: r.creator,
          createdAt: r.created_at,
          progress: Math.min(100, (Number(r.reserve_usdt) / parseFloat(MIGRATION_THRESHOLD)) * 100),
          description: r.description || '',
          website: r.website || '',
        }));
        setTokens(dbTokens);
      }

      // 2. 若合约已配置，以链上数据为准（新合约为空时显示空列表）
      if (BONDING_CURVE_ADDRESS) {
        const chainList = await bondingRef.current.getAllTokens();
        // 链上数据直接作为显示数据，不再依赖 Supabase 白名单过滤
        setTokens(chainList);
        if (chainList.length > 0) {
          const toUpsert = chainList.map(t => ({
            token_address: t.tokenAddress,
            name: t.name,
            symbol: t.symbol,
            decimals: t.decimals,
            total_supply: t.totalSupply,
            creator: t.creator,
            reserve_usdt: parseFloat(t.reserveUSDT),
            token_balance: t.tokenBalance,
            current_price: t.currentPrice,
            migrated: t.migrated,
            active: true,
            created_at: t.createdAt,
            description: t.description || '',
            website: t.website || '',
          }));
          upsertTokens(toUpsert).catch(() => {});
        }
      }
    } finally {
      setLoadingTokens(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadTokens();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Load balances when token selected
  useEffect(() => {
    if (!selectedToken || !web3.account) return;
    const b = bondingRef.current;
    b.getUSDTBalance(web3.account).then(setUsdtBalance);
    b.getTokenBalance(selectedToken.tokenAddress, web3.account, selectedToken.decimals).then(setTokenBalance);
  }, [selectedToken, web3.account]);

  const handleSelectToken = useCallback((token: TokenData) => {
    setSelectedToken(token);
    setPage('trade');
  }, []);

  const handleBuy = useCallback(async (usdtAmount: string, slippage: number) => {
    if (!selectedToken) return false;
    const ok = await bonding.buyTokens(selectedToken.tokenAddress, usdtAmount, slippage);
    if (ok) {
      toast.success(`成功购买 ${selectedToken.symbol}！`);
      loadTokens();
      if (web3.account) {
        bonding.getUSDTBalance(web3.account).then(setUsdtBalance);
        bonding.getTokenBalance(selectedToken.tokenAddress, web3.account, selectedToken.decimals).then(setTokenBalance);
        insertTransaction({
          token_address: selectedToken.tokenAddress,
          tx_hash: `buy_${Date.now()}_${web3.account.slice(-6)}`,
          tx_type: 'buy',
          user_address: web3.account,
          usdt_amount: parseFloat(usdtAmount),
        }).catch(() => {});
      }
    } else if (bonding.error) {
      toast.error(bonding.error);
    }
    return ok;
  }, [selectedToken, bonding, loadTokens, web3.account]);

  const handleSell = useCallback(async (tokenAmount: string, slippage: number) => {
    if (!selectedToken) return false;
    const ok = await bonding.sellTokens(selectedToken.tokenAddress, tokenAmount, selectedToken.decimals, slippage);
    if (ok) {
      toast.success(`成功出售 ${selectedToken.symbol}！`);
      loadTokens();
      if (web3.account) {
        bonding.getUSDTBalance(web3.account).then(setUsdtBalance);
        bonding.getTokenBalance(selectedToken.tokenAddress, web3.account, selectedToken.decimals).then(setTokenBalance);
        insertTransaction({
          token_address: selectedToken.tokenAddress,
          tx_hash: `sell_${Date.now()}_${web3.account.slice(-6)}`,
          tx_type: 'sell',
          user_address: web3.account,
          token_amount: tokenAmount,
        }).catch(() => {});
      }
    } else if (bonding.error) {
      toast.error(bonding.error);
    }
    return ok;
  }, [selectedToken, bonding, loadTokens, web3.account]);

  const handleMigrate = useCallback(async () => {
    if (!selectedToken) return false;
    const ok = await bonding.migrateToken(selectedToken.tokenAddress);
    if (ok) {
      toast.success('流动性迁移到 PancakeSwap 成功！', { duration: 6000 });
      loadTokens();
      setSelectedToken(prev => prev ? { ...prev, migrated: true, active: false } : null);
    } else if (bonding.error) {
      toast.error(bonding.error);
    }
    return ok;
  }, [selectedToken, bonding, loadTokens]);

  const handleAddToken = useCallback(async (tokenAddress: string, initialAmount: string, description: string, website: string) => {
    const ok = await bonding.addToken(tokenAddress, initialAmount, description, website);
    if (ok) {
      toast.success('代币添加成功！');
      // 链上交易成功后，把 description / website 写入 Supabase
      // loadTokens 会同步链上数据，但合约里没有存这两个字段，需单独写入
      await updateTokenMeta(tokenAddress, description.trim(), website.trim());
      loadTokens();
    } else if (bonding.error) {
      toast.error(bonding.error);
    }
    return ok;
  }, [bonding, loadTokens]);

  const handleEstimateBuy = useCallback((amount: string) => {
    if (!selectedToken) return Promise.resolve('0');
    return bonding.estimateBuy(selectedToken.tokenAddress, amount, selectedToken.decimals);
  }, [selectedToken, bonding]);

  const handleEstimateSell = useCallback((amount: string) => {
    if (!selectedToken) return Promise.resolve('0');
    return bonding.estimateSell(selectedToken.tokenAddress, amount, selectedToken.decimals);
  }, [selectedToken, bonding]);

  return (
    <div className="app">
      <Toaster
        position="top-right"
        toastOptions={{
          style: {
            background: '#131929',
            color: '#e8eef7',
            border: '1px solid #1e2d4a',
            borderRadius: '12px',
            fontSize: '14px',
            fontFamily: 'Inter, sans-serif',
          },
          success: { iconTheme: { primary: '#00e676', secondary: '#131929' } },
          error: { iconTheme: { primary: '#ff4d6d', secondary: '#131929' } },
        }}
      />

      <Header
        web3={web3}
        currentPage={page}
        onNavigate={p => {
          setPage(p as Page);
          if (p !== 'trade') setSelectedToken(null);
        }}
      />

      <main className="main-content">
        {/* Contract not deployed warning */}
        {!BONDING_CURVE_ADDRESS && (
          <div className="deploy-warning">
            <span className="deploy-warning-icon">⚠️</span>
            <div>
              <strong>合约尚未部署</strong> — 当前为演示模式。
              请将 <code>BondingCurve.sol</code> 部署到 BSC 主网后，
              在 <code>src/config/contracts.ts</code> 中填入合约地址即可启用完整功能。
            </div>
          </div>
        )}

        {/* Network warning */}
        {web3.isConnected && !web3.isBSC && (
          <div className="network-warning">
            <span>⚠️ 请切换到 BSC 主网（Chain ID: 56）</span>
            <button className="switch-btn" onClick={web3.switchToBSC}>
              切换到 BSC
            </button>
          </div>
        )}

        {/* Pages */}
        {page === 'home' && (
          <MarketPage
            tokens={tokens}
            onSelectToken={handleSelectToken}
            onRefresh={loadTokens}
            loading={loadingTokens}
            onAddToken={() => setShowAddModal(true)}
            account={web3.account}
          />
        )}

        {page === 'trade' && (
          <TradingPanel
            token={selectedToken}
            account={web3.account}
            usdtBalance={usdtBalance}
            tokenBalance={tokenBalance}
            loading={bonding.loading}
            onBuy={handleBuy}
            onSell={handleSell}
            onMigrate={handleMigrate}
            estimateBuy={handleEstimateBuy}
            estimateSell={handleEstimateSell}
            onBack={() => setPage('home')}
          />
        )}

        {page === 'add' && (
          <div className="add-page">
            <div className="add-page-hero">
              <h1 className="add-page-title">添加代币到 Easy 平台</h1>
              <p className="add-page-sub">
                将你的 BEP-20 代币添加到联合曲线交易平台，
                无需创建新代币，直接使用已有代币。
              </p>
              <button
                className="add-page-btn"
                onClick={() => setShowAddModal(true)}
              >
                + 立即添加代币
              </button>
            </div>

            {/* How it works */}
            <div className="how-it-works">
              <h2 className="how-title">工作原理</h2>
              <div className="how-steps">
                {[
                  {
                    num: '01',
                    title: '输入代币地址',
                    desc: '输入你已有的 BEP-20 代币合约地址，系统自动读取代币信息',
                    color: '#00d4ff',
                  },
                  {
                    num: '02',
                    title: '注入初始代币',
                    desc: '向曲线合约注入初始代币数量，这些代币将用于用户购买',
                    color: '#7c4dff',
                  },
                  {
                    num: '03',
                    title: '开始曲线交易',
                    desc: '代币上线后，用户可用 USDT 购买，价格随购买量自动上涨',
                    color: '#00e676',
                  },
                  {
                    num: '04',
                    title: '自动迁移 PancakeSwap',
                    desc: `USDT 储备达到 ${parseInt(MIGRATION_THRESHOLD).toLocaleString()} 后，自动将全部流动性迁移至 PancakeSwap`,
                    color: '#ffd600',
                  },
                ].map((step, i) => (
                  <div key={i} className="how-step">
                    <div className="how-step-num" style={{ color: step.color, borderColor: step.color }}>
                      {step.num}
                    </div>
                    <div className="how-step-content">
                      <div className="how-step-title">{step.title}</div>
                      <div className="how-step-desc">{step.desc}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Curve Formula */}
            <div className="formula-card">
              <h3 className="formula-title">📐 联合曲线公式</h3>
              <div className="formula-content">
                <div className="formula-main">
                  Price(x) = BASE_PRICE + SLOPE × x
                </div>
                <div className="formula-params">
                  <div className="formula-param">
                    <span className="formula-param-name">BASE_PRICE</span>
                    <span className="formula-param-value">= 0.0001 USDT（初始价格）</span>
                  </div>
                  <div className="formula-param">
                    <span className="formula-param-name">SLOPE</span>
                    <span className="formula-param-value">= 1e-10（每卖出1个代币价格增量）</span>
                  </div>
                  <div className="formula-param">
                    <span className="formula-param-name">x</span>
                    <span className="formula-param-value">= 已售出的代币数量</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {page === 'portfolio' && (
          <PortfolioPage
            account={web3.account}
            tokens={tokens}
            provider={web3.provider}
            onSelectToken={handleSelectToken}
          />
        )}

        {page === 'announce' && (
          <AnnouncementPage
            account={web3.account}
            signer={web3.signer}
          />
        )}
      </main>

      {/* Add Token Modal */}
      {showAddModal && (
        <AddTokenModal
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddToken}
          account={web3.account}
          provider={web3.provider}
          loading={bonding.loading}
        />
      )}

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <div className="footer-brand">
            <span className="footer-logo">Easy</span>
            <span className="footer-tagline">BSC Bonding Curve Trading Platform</span>
          </div>
          <div className="footer-links">
            <a href="https://pancakeswap.finance" target="_blank" rel="noopener noreferrer" className="footer-link">
              PancakeSwap
            </a>
            <a href="https://bscscan.com" target="_blank" rel="noopener noreferrer" className="footer-link">
              BSCScan
            </a>
            <a href="https://docs.bnbchain.org" target="_blank" rel="noopener noreferrer" className="footer-link">
              BNB Chain Docs
            </a>
          </div>
          <div className="footer-note">
            ⚠ 本平台仅供技术演示，请自行评估投资风险
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App;
